import React, { useState } from "react";
import api from "../services/api";
import { FaEye, FaEyeSlash, FaLock, FaArrowRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [token2FA, setToken2FA] = useState("");
  const [erro, setErro] = useState("");
  const [senhaVisivel, setSenhaVisivel] = useState(false);
  const [is2FARequired, setIs2FARequired] = useState(false);
  const [carregando, setCarregando] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro("");

    if (!email.trim() || !senha) {
      setErro("Preencha seu e-mail e sua senha para continuar.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email.trim())) {
      setErro("Digite um endereço de e-mail válido.");
      return;
    }

    if (is2FARequired && !token2FA.trim()) {
      setErro("Informe o código de autenticação em duas etapas.");
      return;
    }

    setCarregando(true);

    try {
      const response = await api.post(
        "/login",
        {
          email: email.trim(),
          senha,
          token_2fa: token2FA.trim(),
        },
        { withCredentials: true }
      );

      if (response.data?.error === "Token 2FA é obrigatório") {
        setIs2FARequired(true);
        setErro("Informe o código de autenticação em duas etapas.");
        return;
      }

      const {
        role,
        barbearia_id,
        email: responseEmail,
        token,
      } = response.data;

      if (!role || barbearia_id == null) {
        setErro("Não foi possível concluir o login. Tente novamente.");
        return;
      }

      sessionStorage.setItem("role", role);
      sessionStorage.setItem("barbearia_id", barbearia_id);
      sessionStorage.setItem("email", responseEmail || email.trim());

      // Mantido para compatibilidade com o fluxo atual da aplicação.
      // Se a autenticação já usa cookie HttpOnly do servidor,
      // não é necessário gravar o token novamente pelo JavaScript.
      if (token) {
        document.cookie = `token=${encodeURIComponent(token)}; path=/; secure; samesite=none`;
      }

      navigate("/dashboard");
    } catch (err) {
      console.error(
        "Erro ao tentar fazer login:",
        err.message || err
      );

      if (err.response?.data?.error === "Token 2FA é obrigatório") {
        setIs2FARequired(true);
        setErro("Informe o código de autenticação em duas etapas.");
      } else if (err.response?.status === 403) {
        setErro("Acesso não permitido para esta conta.");
      } else {
        setErro("E-mail ou senha inválidos. Verifique seus dados.");
      }
    } finally {
      setCarregando(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-card">
        <div className="login-brand">
          <img
            src="/logo_cronos.png"
            alt="Cronos Agenda"
            className="login-logo"
          />
        </div>

        <div className="login-heading">
          <span className="login-eyebrow">ÁREA DO CLIENTE</span>
          <h1>Bem-vindo de volta!</h1>
          <p>Entre na sua conta para continuar.</p>
        </div>

        {erro && (
          <div className="login-error" role="alert">
            <span className="login-error-icon">!</span>
            <p>{erro}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <div className="login-field">
            <label htmlFor="login-email">E-mail</label>
            <input
              id="login-email"
              type="email"
              placeholder="voce@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              disabled={carregando}
            />
          </div>

          <div className="login-field">
            <div className="login-label-row">
              <label htmlFor="login-senha">Senha</label>
            </div>

            <div className="login-password-wrap">
              <input
                id="login-senha"
                type={senhaVisivel ? "text" : "password"}
                placeholder="Digite sua senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="current-password"
                required
                disabled={carregando}
              />

              <button
                type="button"
                className="login-toggle-password"
                onClick={() => setSenhaVisivel(!senhaVisivel)}
                aria-label={senhaVisivel ? "Ocultar senha" : "Mostrar senha"}
                aria-pressed={senhaVisivel}
                disabled={carregando}
              >
                {senhaVisivel ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {is2FARequired && (
            <div className="login-field login-2fa-field">
              <label htmlFor="login-2fa">
                Código de autenticação
              </label>
              <input
                id="login-2fa"
                type="text"
                placeholder="Digite o código de 2FA"
                value={token2FA}
                onChange={(e) => setToken2FA(e.target.value)}
                autoComplete="one-time-code"
                inputMode="numeric"
                required
                disabled={carregando}
              />
              <span className="login-field-hint">
                Abra seu aplicativo autenticador para consultar o código.
              </span>
            </div>
          )}

          <button
            type="submit"
            className="login-submit"
            disabled={carregando}
          >
            {carregando ? (
              <>
                <span className="login-spinner" />
                Entrando...
              </>
            ) : (
              <>
                Entrar na minha conta
                <FaArrowRight />
              </>
            )}
          </button>
        </form>

        <div className="login-security">
          <FaLock />
          <span>Seu acesso é protegido e seguro</span>
        </div>

        <footer className="login-footer">
          <span>© {new Date().getFullYear()} Cronos Agenda</span>
          <span>Organize seu tempo.</span>
        </footer>
      </section>
    </main>
  );
};

export default Login;